import { useQuery } from '@apollo/client';
import {
  Archive,
  ArrowCounterClockwise,
  Copy,
  FileCsv,
  MagnifyingGlass,
  PauseCircle,
  Pencil,
  PlayCircle,
  Trash,
  UserPlus,
  XCircle,
} from '@phosphor-icons/react';
import { useDebounce, useLocalStorageState } from 'ahooks';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { CopyableField } from 'src/components/CopyableField';
import { Input } from 'src/components/Input';
import { Modal } from 'src/components/Modal';
import { SegmentedButtons } from 'src/components/SegmentedButtons';
import {
  SideDropAction,
  SideDropActions,
} from 'src/components/SideDropActions';
import { Switcher } from 'src/components/Switcher';
import Table, { Header, Row } from 'src/components/Table';
import { WarningPanel } from 'src/components/WarningPanel';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import {
  GET_WORKFLOW_STATS,
  GetWorkflowStatsQueryData,
  GetWorkflowStatsQueryVariables,
} from 'src/graphql/queries';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, can, Subject } from 'src/hooks/usePermissions';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import {
  PaginatedResponse,
  WorkflowExecutionThreadStatus,
  WorkflowStatus,
  WorkflowWithStatus,
} from 'src/interfaces/workflows.interface';
import { friendlyDateFormatter } from 'src/utils/date';
import { generateReferenceIdForDuplicate } from 'src/utils/referenceId';

interface AutomationsTabProps {
  onNewCampaign?: () => void;
  beta?: boolean;
}

interface WorkflowRow {
  referenceId: string;
  referenceIdDisplay: ReactElement;
  version: string | number;
  label: string;
  createdAt: string;
  createdBy: string;
  status: string;
  scheduled: number;
  paused: number;
  completed: number;
  errored: number;
  action: ReactElement;
}

enum WorkflowViewStatus {
  IN_USE = 'in_use',
  ARCHIVED = 'archived',
}

function getCampaignStartDate(workflow: WorkflowWithStatus): {
  startDate: string | null;
  startDateIso: string | null;
} {
  if (workflow.contactWindowId) {
    const startDateIso = workflow.config.start_at;
    const startDateTz = workflow.config.start_tz;

    if (!startDateIso || !startDateTz) {
      return { startDate: null, startDateIso: null };
    }

    const startDate = toZonedTime(startDateIso, startDateTz);
    return {
      startDate: format(startDate, 'yyyy-MM-dd'),
      startDateIso,
    };
  }

  const schedule = workflow.config.schedule;
  const startDateIso = schedule?.startDate;
  const startDateTz = schedule?.timeZone;

  if (!startDateIso || !startDateTz) {
    return { startDate: null, startDateIso: null };
  }

  const startDate = toZonedTime(startDateIso, startDateTz);
  return {
    startDate: format(startDate, 'yyyy-MM-dd'),
    startDateIso,
  };
}

export const AutomationsTab = ({
  onNewCampaign,
  beta = false,
}: AutomationsTabProps) => {
  const { get, post, remove } = useAuthedRequest();
  const notification = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const { role, environment } = useEnvironment();
  const environmentId = environment?.envId;

  const [rows, setRows] = useState<WorkflowRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string | null;
  }>({ key: 'createdAt', direction: 'desc' });
  const [filter, setFilter] = useState('');
  const debouncedFilter = useDebounce(filter, { wait: 500 });
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [deactivateCampaignModalOpen, setDeactivateCampaignModalOpen] =
    useState(false);
  const [activateCampaignModalOpen, setActivateCampaignModalOpen] =
    useState(false);
  const [cancelScheduledCallsModalOpen, setCancelScheduledCallsModalOpen] =
    useState(false);
  const [selectedWorkflow, setSelectedWorkflow] =
    useState<WorkflowWithStatus | null>(null);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [workflowReferenceIds, setWorkflowReferenceIds] = useState<string[]>(
    [],
  );
  const [useBetaCampaigns, setUseBetaCampaigns] = useLocalStorageState<boolean>(
    '@control-plane/use-beta-campaigns',
    { defaultValue: false, listenStorageChange: true },
  );
  const [viewStatus, setViewStatus] = useState<WorkflowViewStatus>(
    WorkflowViewStatus.IN_USE,
  );

  const hasShownErrorRef = useRef(false);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedWorkflow(null);
    setDeleteModalOpen(false);
    setArchiveModalOpen(false);
    setDeactivateCampaignModalOpen(false);
    setActivateCampaignModalOpen(false);
    setCancelScheduledCallsModalOpen(false);
  }, [viewStatus]);

  const {
    data: workflowStatsData,
    loading: workflowStatsLoading,
    error: workflowStatsError,
  } = useQuery<GetWorkflowStatsQueryData, GetWorkflowStatsQueryVariables>(
    GET_WORKFLOW_STATS,
    {
      variables: {
        environmentId: environmentId!,
        workflowReferenceIds,
      },
      skip: workflowReferenceIds.length === 0 || !environmentId,
    },
  );

  const headers: Header[] = [
    {
      key: 'referenceIdDisplay',
      label: 'ID',
      width: '8rem',
      disableSorting: true,
    },
    { key: 'label', label: 'Campaign Name', width: '20%' },
    { key: 'createdAt', label: 'Created At', width: '12%' },
    { key: 'status', label: 'Status', width: '8%' },
    {
      key: 'scheduled',
      label: '# Scheduled',
      width: '6rem',
      disableSorting: true,
    },
    {
      key: 'paused',
      label: '# Inactive',
      width: '6rem',
      disableSorting: true,
    },
    {
      key: 'completed',
      label: '# Completed',
      width: '6rem',
      disableSorting: true,
    },
    { key: 'errored', label: '# Errored', width: '6rem', disableSorting: true },
    {
      key: 'action',
      label: 'Actions',
      width: '2%',
      disableSorting: true,
      align: 'center',
    },
  ];

  const handleAddContacts = useCallback(
    (workflow: WorkflowWithStatus) => {
      navigate(`/campaigns/${workflow.referenceId}/contacts`);
    },
    [navigate],
  );

  const fetchWorkflows = useCallback(
    async (page: number, pageSize: number) => {
      setLoading(true);
      try {
        const statuses =
          viewStatus === WorkflowViewStatus.ARCHIVED
            ? [WorkflowStatus.WORKFLOW_ARCHIVED]
            : [WorkflowStatus.WORKFLOW_ACTIVE, WorkflowStatus.WORKFLOW_PAUSED];

        const queryObject: QueryObject = {
          page: String(page),
          size: String(pageSize),
          filters: JSON.stringify({
            label: debouncedFilter,
            statuses,
          }),
        };

        if (sortConfig.key) {
          queryObject.sort_column = sortConfig.key;
          queryObject.sort_desc =
            sortConfig.direction === 'desc' ? 'true' : 'false';
        }

        const query = new URLSearchParams(
          queryObject as Record<string, string>,
        );

        const data: PaginatedResponse<WorkflowWithStatus> = await get(
          `/workflows?${query.toString()}`,
        );
        // Map each workflow into a row for the table.

        const workflowsIds: string[] = [];
        const items: WorkflowRow[] = data.items.map((workflow) => {
          workflowsIds.push(workflow.referenceId);

          const { startDate, startDateIso } = getCampaignStartDate(workflow);
          let status = '';

          switch (workflow.status) {
            case WorkflowStatus.WORKFLOW_ACTIVE:
              // If the campaign is scheduled to start in the future, show the start date.
              if (startDate) {
                if (new Date(startDateIso!) > new Date()) {
                  status = `Scheduled to start on ${startDate}`;
                } else {
                  status = 'Active';
                }
              } else {
                status = 'Active';
              }
              break;
            case WorkflowStatus.WORKFLOW_PAUSED:
              status = 'Inactive';
              break;
            case WorkflowStatus.WORKFLOW_ARCHIVED:
              status = 'Archived';
              break;
          }

          const actions: SideDropAction[] = [
            {
              label: can(role, 'update', Subject.CAMPAIGNS) ? 'Edit' : 'View',
              Icon: Pencil,
              onClick: () =>
                navigate(
                  beta
                    ? `/beta/campaigns/overview/${workflow.referenceId}`
                    : `/campaigns/edit/${workflow.referenceId}`,
                ),
              show: workflow.status !== WorkflowStatus.WORKFLOW_ARCHIVED,
            },
            {
              label: 'Add Contacts',
              Icon: UserPlus,
              onClick: () => handleAddContacts(workflow),
              show:
                can(role, 'update', Subject.CAMPAIGNS) &&
                workflow.status !== WorkflowStatus.WORKFLOW_ARCHIVED,
            },
          ];
          if (workflow.status === WorkflowStatus.WORKFLOW_PAUSED) {
            actions.push({
              label: 'Activate Campaign',
              Icon: PlayCircle,
              onClick: () => openActivateCampaignModal(workflow),
            });
          } else if (workflow.status === WorkflowStatus.WORKFLOW_ACTIVE) {
            actions.push({
              label: 'Deactivate Campaign',
              Icon: PauseCircle,
              onClick: () => openDeactivateCampaignModal(workflow),
            });
          }

          if (workflow.status !== WorkflowStatus.WORKFLOW_ARCHIVED) {
            actions.push({
              label: 'Duplicate Campaign',
              Icon: Copy,
              onClick: () => handleDuplicateCampaign(workflow),
              show: can(role, 'create', Subject.CAMPAIGNS),
            });
            actions.push({
              label: 'Cancel Scheduled Calls',
              Icon: XCircle,
              onClick: () => openCancelScheduledCallsModal(workflow),
              show: can(role, 'update', Subject.CAMPAIGNS),
            });
            actions.push({
              label: 'Export Calls CSV',
              Icon: FileCsv,
              onClick: () =>
                navigate(
                  `/calls?workflow_reference_id=${workflow.referenceId}`,
                ),
              show: can(role, 'read', Subject.CALLS),
            });
            actions.push({
              label: 'Archive',
              Icon: Archive,
              color: 'error',
              onClick: () => openArchiveWorkflowModal(workflow),
              show: can(role, 'delete', Subject.CAMPAIGNS),
            });
          } else {
            actions.push({
              label: 'Unarchive',
              Icon: ArrowCounterClockwise,
              onClick: () => handleUnarchiveWorkflow(workflow),
              show: can(role, 'update', Subject.CAMPAIGNS),
            });
            actions.push({
              label: 'Delete',
              Icon: Trash,
              color: 'error',
              onClick: () => openDeleteWorfklowModal(workflow),
              show: can(role, 'delete', Subject.CAMPAIGNS),
            });
          }

          return {
            referenceId: workflow.referenceId,
            referenceIdDisplay: (
              <CopyableField
                value={workflow.referenceId}
                notification={notification}
              />
            ),
            version: workflow.version,
            label: workflow.label,
            createdAt: friendlyDateFormatter(new Date(workflow.createdAt)),
            createdBy: workflow.environmentId ? 'User' : 'Fluents',
            status,
            scheduled: 0,
            paused: 0,
            completed: 0,
            errored: 0,
            action: (
              <div
                className="flex justify-center items-center"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <SideDropActions options={actions} />
              </div>
            ),
          };
        });
        setWorkflowReferenceIds(workflowsIds);
        setRows(items);
        setTotalItems(data.total);
      } catch (error) {
        console.error('Error fetching workflows:', error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      beta,
      debouncedFilter,
      sortConfig.key,
      sortConfig.direction,
      get,
      role,
      notification,
      navigate,
      handleAddContacts,
      viewStatus,
    ],
  );

  useEffect(() => {
    fetchWorkflows(currentPage, currentPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentPageSize, sortConfig, debouncedFilter, viewStatus]);

  const handlePageChange = (page: number, perPage: number) => {
    setCurrentPage(page);
    setCurrentPageSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setCurrentPageSize(perPage);
    setCurrentPage(1);
  };

  const handleSort = (key: string, direction: string) => {
    setSortConfig({ key, direction });
  };

  const openCancelScheduledCallsModal = (workflow: WorkflowWithStatus) => {
    setSelectedWorkflow(workflow);
    setCancelScheduledCallsModalOpen(true);
  };

  const openDeleteWorfklowModal = (workflow: WorkflowWithStatus) => {
    setSelectedWorkflow(workflow);
    setDeleteModalOpen(true);
  };

  const openDeactivateCampaignModal = (workflow: WorkflowWithStatus) => {
    setSelectedWorkflow(workflow);
    setDeactivateCampaignModalOpen(true);
  };

  const openActivateCampaignModal = (workflow: WorkflowWithStatus) => {
    setSelectedWorkflow(workflow);
    setActivateCampaignModalOpen(true);
  };

  const openArchiveWorkflowModal = (workflow: WorkflowWithStatus) => {
    setSelectedWorkflow(workflow);
    setArchiveModalOpen(true);
  };

  const handleArchiveWorkflow = async () => {
    if (!selectedWorkflow) return;
    setIsActionLoading(true);
    try {
      await post(`/workflows/${selectedWorkflow.referenceId}/archive`, {});
      notification.success('Successfully archived campaign');
      setArchiveModalOpen(false);
      fetchWorkflows(currentPage, currentPageSize);
    } catch {
      notification.error('Failed to archive campaign');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleUnarchiveWorkflow = async (workflow: WorkflowWithStatus) => {
    try {
      // Unarchiving the campaign can take some time, so notify the user that it's happening.
      notification.success('Unarchiving campaign...');
      await post(`/workflows/${workflow.referenceId}/unarchive`, {});
      notification.success('Successfully unarchived campaign');
      fetchWorkflows(currentPage, currentPageSize);
    } catch {
      notification.error('Failed to unarchive campaign');
    }
  };

  const handleRemoveWorkflow = async () => {
    setIsActionLoading(true);
    try {
      await remove(`/workflows/${selectedWorkflow?.referenceId}`);
      notification.success('Successfully deleted campaign');
      setDeleteModalOpen(false);
      setCurrentPage(1);
      fetchWorkflows(1, currentPageSize);
    } catch {
      notification.error('Failed to delete campaign');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleCancelScheduledCalls = async () => {
    if (!selectedWorkflow) return;
    setIsActionLoading(true);
    try {
      await post(`/workflows/${selectedWorkflow.referenceId}/cancel`, {});
      notification.success('Successfully cancelled scheduled calls');
      setCancelScheduledCallsModalOpen(false);
    } catch (error) {
      notification.error('Failed to cancel scheduled calls');
      console.error(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDuplicateCampaign = async (workflow: WorkflowWithStatus) => {
    if (!workflow) return;
    try {
      const fetchedWorkflow = await get(`/workflows/${workflow.referenceId}`);
      if (!fetchedWorkflow) {
        notification.error('Failed to duplicate campaign: campaign not found');
        return;
      }
      const duplicatedWorkflow = await post(`/workflows`, {
        ...fetchedWorkflow,
        referenceId: generateReferenceIdForDuplicate(
          fetchedWorkflow.referenceId,
        ),
        version: 1,
        id: undefined,
        createdAt: undefined,
        updatedAt: undefined,
      });
      if (!duplicatedWorkflow) {
        notification.error('Failed to duplicate campaign');
        return;
      }
      notification.success('Successfully duplicated campaign');
      fetchWorkflows(currentPage, currentPageSize);
    } catch (error) {
      notification.error('Failed to duplicate campaign');
      console.error(error);
    }
  };

  const handleDeactivateCampaign = async () => {
    if (!selectedWorkflow) return;
    setIsActionLoading(true);
    try {
      await post(`/workflows/${selectedWorkflow.referenceId}/pause`, {});
      notification.success('Successfully deactivated campaign');
      setDeactivateCampaignModalOpen(false);
      fetchWorkflows(currentPage, currentPageSize);
    } catch (error) {
      notification.error('Failed to deactivate campaign');
      console.error(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleActivateCampaign = async () => {
    if (!selectedWorkflow) return;
    setIsActionLoading(true);
    try {
      await post(`/workflows/${selectedWorkflow.referenceId}/unpause`, {});
      notification.success('Successfully activated campaign');
      setActivateCampaignModalOpen(false);
      fetchWorkflows(currentPage, currentPageSize);
    } catch (error) {
      notification.error('Failed to activate campaign');
      console.error(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  useEffect(() => {
    if (workflowStatsLoading || !workflowStatsData) return;

    const statusMap = new Map<string, Map<string, number>>();

    workflowStatsData.getWorkflowStats.forEach((stat) => {
      const { workflowReferenceId, status, count } = stat;

      if (!statusMap.has(workflowReferenceId)) {
        statusMap.set(workflowReferenceId, new Map<string, number>());
      }

      const workflowStatusMap = statusMap.get(workflowReferenceId)!;
      workflowStatusMap.set(status, count);
    });

    setRows((currentRows) => {
      return currentRows.map((row) => {
        let workflowStatusMap = statusMap.get(row.referenceId);
        if (!workflowStatusMap) {
          workflowStatusMap = new Map<string, number>();
        }

        const scheduled =
          (workflowStatusMap.get(WorkflowExecutionThreadStatus.RUNNING) || 0) +
          (workflowStatusMap.get(WorkflowExecutionThreadStatus.RATE_LIMITED) ||
            0);
        const paused =
          (workflowStatusMap.get(WorkflowExecutionThreadStatus.PAUSED) || 0) +
          (workflowStatusMap.get(WorkflowExecutionThreadStatus.UNSCHEDULED) ||
            0);
        const completed =
          workflowStatusMap.get(WorkflowExecutionThreadStatus.COMPLETED) || 0;
        const errored =
          workflowStatusMap.get(WorkflowExecutionThreadStatus.ERRORED) || 0;

        return {
          ...row,
          scheduled,
          paused,
          completed,
          errored,
        };
      });
    });
  }, [workflowStatsData, workflowStatsLoading, workflowReferenceIds]);

  const renderedRows: Row[] = rows.map((row) => {
    return {
      ...row,
      scheduled: workflowStatsLoading ? (
        <Skeleton />
      ) : row.scheduled > 0 ? (
        row.scheduled.toLocaleString()
      ) : (
        '—'
      ),
      paused: workflowStatsLoading ? (
        <Skeleton />
      ) : row.paused > 0 ? (
        row.paused.toLocaleString()
      ) : (
        '—'
      ),
      completed: workflowStatsLoading ? (
        <Skeleton />
      ) : row.completed > 0 ? (
        row.completed.toLocaleString()
      ) : (
        '—'
      ),
      errored: workflowStatsLoading ? (
        <Skeleton />
      ) : row.errored > 0 ? (
        row.errored.toLocaleString()
      ) : (
        '—'
      ),
    };
  });

  useEffect(() => {
    if (
      workflowStatsError &&
      workflowReferenceIds.length > 0 &&
      !hasShownErrorRef.current
    ) {
      if (process.env.REACT_APP_GRAPHQL_IGNORE_ERRORS) {
        console.warn('GraphQL error ignored:', workflowStatsError);
      } else {
        notification.error('Failed to load campaign statistics.');
      }
      hasShownErrorRef.current = true;
    }
  }, [workflowStatsError, workflowReferenceIds.length, notification]);

  useEffect(() => {
    hasShownErrorRef.current = false;
  }, [workflowReferenceIds]);

  useEffect(() => {
    const errorMessage = location.state?.error;
    if (errorMessage) {
      const errorKey = `campaign-not-found-${location.pathname}`;
      const hasShownError = sessionStorage.getItem(errorKey);

      if (!hasShownError) {
        sessionStorage.setItem(errorKey, 'true');
        notification.error(errorMessage);
      }
    }
  }, [location.state?.error, notification, location.pathname]);

  useEffect(() => {
    const isOnBetaRoute = location.pathname.startsWith('/beta/campaigns');
    const shouldBeOnBetaRoute = useBetaCampaigns ?? false;

    // Only navigate if we're on the wrong route
    if (shouldBeOnBetaRoute && !isOnBetaRoute) {
      navigate('/beta/campaigns');
    } else if (!shouldBeOnBetaRoute && isOnBetaRoute) {
      navigate('/campaigns');
    }
  }, [useBetaCampaigns, location.pathname, navigate]);

  return (
    <ContentWrapper>
      <div className="flex flex-col xl:flex-row justify-between xl:items-center mb-6 gap-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 w-full xl:w-auto">
          <div className="w-full md:w-96">
            <Can operation="read" subject={Subject.CAMPAIGNS}>
              <Input
                type="text"
                required
                placeholder="Search campaigns"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') {
                    fetchWorkflows(currentPage, currentPageSize);
                  }
                }}
                icon={<MagnifyingGlass size={20} />}
                iconPosition="left"
                inputAction={() => fetchWorkflows(currentPage, currentPageSize)}
              />
            </Can>
          </div>
          <SegmentedButtons
            value={viewStatus}
            onValueChange={(value) =>
              setViewStatus(value as WorkflowViewStatus)
            }
            buttons={[
              { value: WorkflowViewStatus.IN_USE, label: 'In Use' },
              { value: WorkflowViewStatus.ARCHIVED, label: 'Archived' },
            ]}
          />
        </div>
        <div className="flex justify-start xl:justify-end gap-4">
          <div className="flex items-center gap-2">
            <span className="text-neutral-500">Use beta campaigns</span>
            <Switcher
              enabled={useBetaCampaigns ?? false}
              setEnabled={(value) => setUseBetaCampaigns(value)}
              color="primary"
              ariaLabel="Use beta campaigns"
            />
          </div>

          <Can operation="create" subject={Subject.CAMPAIGNS}>
            <Button
              onClick={() =>
                onNewCampaign ? onNewCampaign() : navigate('/campaigns/create')
              }
              className="h-11 whitespace-nowrap"
            >
              New Campaign
            </Button>
          </Can>
        </div>
      </div>

      <Can operation="read" subject={Subject.CAMPAIGNS}>
        <div className="text-right text-neutral-500 italic text-sm mb-4">
          Campaign statistics may be delayed by up to 4 hours.
        </div>
        <Table
          headers={headers}
          rows={renderedRows}
          totalItems={totalItems}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onSort={handleSort}
          loading={loading}
          setSize={handleSizeChange}
          defaultSize={currentPageSize}
          sortConfig={sortConfig}
          onRowClick={(index: number) => {
            const row = rows[index];
            if (row?.referenceId) {
              navigate(
                beta
                  ? `/beta/campaigns/overview/${row.referenceId}`
                  : `/campaigns/edit/${row.referenceId}`,
              );
            }
          }}
        />
      </Can>
      {/* Archive Campaign Modal */}
      <Modal
        title={`Archive Campaign: ${selectedWorkflow?.label || ''}`}
        isOpen={archiveModalOpen}
        onClose={() => {
          setArchiveModalOpen(false);
          setSelectedWorkflow(null);
        }}
        actionButton={
          <Button onClick={handleArchiveWorkflow} disabled={isActionLoading}>
            {isActionLoading ? 'Archiving...' : 'Archive Campaign'}
          </Button>
        }
        className="w-auto max-w-md"
      >
        <WarningPanel title="Warning" show className="my-4">
          Archiving will cancel all pending outreach and prevent new outreach.
        </WarningPanel>
        <div className="mb-2">
          Are you sure you want to archive this campaign?
        </div>
      </Modal>
      {/* Delete Campaign Modal */}
      <Modal
        title={`Delete Campaign: ${selectedWorkflow?.label || ''}`}
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedWorkflow(null);
        }}
        actionButton={
          <Button onClick={handleRemoveWorkflow} disabled={isActionLoading}>
            {isActionLoading ? 'Deleting...' : 'Delete Campaign'}
          </Button>
        }
        className="w-auto max-w-md"
      >
        <WarningPanel title="Warning" show className="my-4">
          This action is irreversible. All campaign data will be permanently
          deleted and new outreach will not be allowed.
        </WarningPanel>
        <div className="mb-2">
          Are you sure you want to delete this campaign?
        </div>
      </Modal>
      {/* Cancel Scheduled Calls Modal */}
      <Modal
        title={`Cancel Scheduled Calls: ${selectedWorkflow?.label || ''}`}
        isOpen={cancelScheduledCallsModalOpen}
        onClose={() => {
          setCancelScheduledCallsModalOpen(false);
          setSelectedWorkflow(null);
        }}
        actionButton={
          <Button
            onClick={handleCancelScheduledCalls}
            disabled={isActionLoading}
          >
            {isActionLoading ? 'Cancelling...' : 'Cancel Scheduled Calls'}
          </Button>
        }
        className="w-auto max-w-md"
      >
        <div className="my-4">
          Are you sure you want to cancel all scheduled calls for this campaign?
        </div>
      </Modal>
      {/* Deactivate Campaign Modal */}
      <Modal
        title={`Deactivate Campaign: ${selectedWorkflow?.label || ''}`}
        isOpen={deactivateCampaignModalOpen}
        onClose={() => {
          setDeactivateCampaignModalOpen(false);
          setSelectedWorkflow(null);
        }}
        actionButton={
          <Button onClick={handleDeactivateCampaign} disabled={isActionLoading}>
            {isActionLoading ? 'Deactivating...' : 'Deactivate Campaign'}
          </Button>
        }
        className="w-auto max-w-md"
      >
        <div className="my-4">
          Are you sure you want to deactivate this campaign?
        </div>
      </Modal>
      {/* Activate Campaign Modal */}
      <Modal
        title={`Activate Campaign: ${selectedWorkflow?.label || ''}`}
        isOpen={activateCampaignModalOpen}
        onClose={() => {
          setActivateCampaignModalOpen(false);
          setSelectedWorkflow(null);
        }}
        actionButton={
          <Button onClick={handleActivateCampaign} disabled={isActionLoading}>
            {isActionLoading ? 'Activating...' : 'Activate Campaign'}
          </Button>
        }
        className="w-auto max-w-md"
      >
        <div className="my-4">
          Are you sure you want to activate this campaign?
        </div>
      </Modal>
    </ContentWrapper>
  );
};
